/**
 * THE WEAVE CONVERSATION ON LANE V2 — the same transcript UX, fed by `laneV2SessionStore`.
 *
 * `WeaveTranscript` is the run-keyed host (v1: `runStateStore`, `/api/runs`); this is the
 * session-keyed one (`/api/sessions/**`, the read model, the session stream). Everything a reader
 * sees is shared: `TranscriptColumn` (scroller, pin/follow, jump pill, empty state), `TurnView` and
 * its blocks (prose, fenced code → `ChatCodeBlock`/`JsonRecord`, entity links), `StreamStatusView`,
 * `TurnCompletion` (copy / export / follow-ups / feedback). What differs is where the turns come
 * from — `conversationEntries()` shapes R5 turns and the client's own echoes into protocol
 * `Turn`s — and what else lives in the flow.
 *
 * ONE STORY PER TURN. A turn is one evolving object: the question, then `LaneV2TurnStory` — the
 * single owner of the turn's state (`presentTurn`): "Working · Reading a component", the phase row,
 * the activity rows, the elapsed clock, how it ended — then the answer's prose, then the decision
 * if there is one. No other block states a status. The full task (plan revision, build, package,
 * validation, transition history) is the workspace Task section, one click away.
 *
 * THE DECISION SITS ABOVE THE COMPOSER. The newest pending ask renders in the column's sticky dock,
 * so it stays in view while the reader scrolls back, and the composer underneath is where a typed
 * reply answers it. Older pending asks (rare) stay at their place in the flow.
 *
 * A SETTLED QUESTION IS PART OF A STORY, never a loose line. A task's answered asks are rows of
 * that task's history ("You approved the plan", inside the story strip); a task-less one (a clarify)
 * is one line under the turn that raised it. Nothing "Answered" floats between turns.
 *
 * VIEW / WIRED SPLIT, like `WeaveTranscript`'s neighbours: `LaneV2TranscriptView` takes state and
 * handlers as props and is what the node test suite renders; `LaneV2Transcript` reads the store.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useShallow } from "zustand/react/shallow";
import toast from "react-hot-toast";
import { ClosingLine, TurnView } from "./TurnView";
import { TurnCompletion, buildCodeInterceptor } from "./TranscriptBlocks";
import { EmptyConversation, TranscriptColumn, useFollowScroll } from "./TranscriptColumn";
import { useEntityLinks } from "./useEntityLinks";
import { renderEvidenceLink } from "@/components/molecules/multi-agent/EvidenceLinkChip";
import { StreamStatusView } from "@/components/molecules/multi-agent/StreamStatus";
import { ArtifactLinkContext } from "@/components/organisms/studio/SkillResultMarkdown";
import { LaneV2TurnUsageChip } from "@/components/molecules/multi-agent/UsageMeter";
import { NO_SOURCES_LABEL } from "@/components/molecules/multi-agent/SourcesReadBlock";
import { formatCost } from "@/services/multi-agent/usageMeter";
import { readNothing, readsOnlyConnectedTools, splitSourcesTail } from "@/services/multi-agent/laneV2/sourcesTail";
import { extractFollowUpEntities, followUpsForLane } from "@/components/molecules/multi-agent/FollowUpChips";
import { isProductionEnvironment } from "@/components/molecules/org-admin/connectionEnvironment";
import { LaneV2ApprovedPlan, LaneV2AskBlock, LaneV2AskRecord, approvedPlanAsk, planAskPending } from "../laneV2/LaneV2AskBlock";
import { LaneV2TaskOutcome, showsOutcome } from "../laneV2/LaneV2TaskOutcome";
import { CompactionDivider } from "@/components/molecules/multi-agent/CompactionDivider";
import { useLaneV2AutoOpen } from "@/hooks/multi-agent/useLaneV2AutoOpen";
import { useSessionCompaction } from "@/hooks/multi-agent/useSessionCompaction";
import type { SessionCompaction } from "@/services/multi-agent/laneV2/types";
import { LaneV2TurnStory } from "../laneV2/LaneV2TurnStory";
import { buildExportMarkdown } from "@/services/multi-agent/exportAppendix";
import { downloadMarkdownAs } from "@/services/multi-agent/exportArtifact";
import type { RespondBody } from "@/services/multi-agent/interactions";
import {
  SESSION_STREAM_LOST_DETAIL,
  answerMarkdownOf,
  conversationEntries,
  conversationTurn,
  lastTurnEntry,
  lastTurnRaisedConnectorWrite,
  streamStatusViewFor,
  turnClosingLine,
  turnInFlight,
  withoutForwardDeployAsks,
  cancelRefusalCopy,
  refSuffix,
  turnFailureWords,
  type ConversationTurnEntry,
} from "@/services/multi-agent/laneV2/conversation";
import { plainUserText } from "@/services/multi-agent/laneV2/followUps";
import { answerDisplay } from "@/components/organisms/multi-agent/laneV2/PlanQuestionsCard";
import { presentTurn, taskForTurn, type StoryContext } from "@/services/multi-agent/laneV2/presentation";
import { pendingAsks, type LaneV2State } from "@/services/multi-agent/laneV2/reduce";
import type { AskView, SaveBranchIn } from "@/services/multi-agent/laneV2/types";
import type { SaveBranchSettled } from "@/services/multi-agent/laneV2/branchSave";
import { partText, workspaceOpenFor, type OpenableSection } from "@/services/multi-agent/transcript";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { useLaneV2SessionStore, type SaveBranchRunOpts } from "@/stores/laneV2SessionStore";
import {
  selectWorkspaceRef,
  selectWorkspaceSection,
  useWeaveWorkspaceStore,
  type CodePayload,
  type WorkspaceSection,
} from "@/stores/weaveWorkspaceStore";

export type LaneV2TranscriptState = Pick<
  LaneV2State,
  | "sessionId"
  | "draft"
  | "tasks"
  | "snapshots"
  | "asks"
  | "turns"
  | "activeTurnId"
  | "localTurns"
  | "streamStatus"
  | "error"
  | "usageTotal"
  | "offline"
  | "connectorWrites"
  | "activity"
  | "workingSince"
> &
  Partial<Pick<LaneV2State, "liveDraft">>;

export interface LaneV2TranscriptViewProps {
  state: LaneV2TranscriptState;
  org?: { is_production?: boolean; org_name?: string } | null;
  /** Throws the typed error on refusal — the ask block turns it into copy. */
  onAnswerAsk: (ask: AskView, body: RespondBody) => Promise<void>;
  /** Re-read the asks and the ask's task after a stale refusal (never an automatic retry). */
  onStaleAsk: (ask: AskView) => void;
  /** Open (or close) the workspace Task section on one task. */
  /** `openedBy: "auto"` when the screen opened it on its own (the plan put up for approval), so a later auto-open may replace it. */
  onOpenTask: (taskId: string, openedBy?: "auto" | "user") => void;
  /** Open the Task section on one work item's diff — the outcome card's "View diff". Omitted: no such link. */
  onOpenTaskItem?: (taskId: string, workItemId: string) => void;
  /** Save to a branch (R0 `branch_save` only); omitted, the outcome card offers no Save. */
  onSaveBranch?: (taskId: string, body: SaveBranchIn, opts?: SaveBranchRunOpts) => Promise<SaveBranchSettled>;
  /** Re-run the check against the org. Omitted, the outcome card offers no Validate. */
  onValidateTask?: (taskId: string, idempotencyKey: string) => Promise<unknown>;
  onRequestChanges?: (taskId: string, text: string, idempotencyKey: string) => Promise<unknown>;
  onDiscardTask?: (taskId: string, idempotencyKey: string) => Promise<unknown>;
  onReconnect: () => Promise<void> | void;
  /** Put a question in the composer (follow-up chips, starter prompts). Never a send. */
  onAsk?: (question: string) => void;
  /** Send a message now ("Try again" re-sends the last request, "Ask what happened" sends its question). Omitted: those fall back to `onAsk`. */
  onSend?: (text: string) => void;
  /** Cancel a task from the chat (the story strip's "Cancel task"). Omitted: no button. */
  onCancelTask?: (taskId: string) => Promise<{ cancelled: boolean; reason: string | null }>;
  /** "Pause after this component" / "Resume" (R3 `task_controls`). Resolve to the typed status; throw `LaneV2Error` on refusal. Omitted: no button. */
  onHoldTask?: (taskId: string) => Promise<{ status: string }>;
  onResumeTask?: (taskId: string) => Promise<{ status: string }>;
  /** Open a fenced block from an answer — the route's editor tab, or the workspace's viewer. */
  onOpenCode: (payload: CodePayload) => void;
  onOpenWorkspace: (section: OpenableSection, focusRef: string | null) => void;
  activeSection: WorkspaceSection | null;
  activeRef: string | null;
  className?: string;
  /** The session's live compaction (`useSessionCompaction`): draws the "Conversation compacted" divider. */
  compaction?: SessionCompaction | null;
}

/** The R0 `offline` kill switch: new work refuses (Send / a granting ask answer); declines and
 *  cancel stay enabled (the server itself never gates those). */
function OfflineBanner() {
  return (
    <p
      role="status"
      className="rounded border border-ma-warn-line bg-ma-warn-soft px-2.5 py-1.5 text-[12px] text-ma-warn-text"
      data-lane-v2-offline=""
    >
      This is turned off for maintenance right now. You can still decline a question or cancel a task. Sending a
      new message or approving is disabled until it is back.
    </p>
  );
}

/** A failure line: plain words, with the machine code kept only as a muted "(ref CODE)" (UX map C13). */
export function FailureLine({ text, tone, refCode }: { text: string; tone: "ok" | "warn" | "bad"; refCode: string | null }) {
  if (!refCode) return <ClosingLine text={text} tone={tone} />;
  return (
    <p role="status" className="text-[13px] text-[var(--ma-state-err-text)]" data-lane-v2-failure-line="">
      {text}
      {text ? " " : ""}
      <span className="text-muted-foreground" data-lane-v2-failure-ref="">{refSuffix(refCode)}</span>
    </p>
  );
}

/**
 * A settled turn whose task a LATER turn carries on: its story strip would repeat the task's live state ("Waiting for you ·
 * Changes ready") under every earlier turn — four copies on dev 2026-10-31, one under a turn the server had refused. It
 * becomes one line pointing at the newest turn. A failed or stopped turn keeps its own story: that ending is the turn's.
 */
export function storyMovedOn(
  entry: { key: string; taskId?: string | null; live?: boolean },
  storyKind: string,
  outcomeOwner: string | undefined,
  superseded = false,
): boolean {
  // an answered turn shows no strip at all, so it needs no pointer either (a question asked while a change rests)
  // A REPLACED change ended by a newer request, not by this turn: its earlier turns fold like any other (dev 2026-11-02: "Replaced …
  // still savable" under both the plan turn and the card's turn). A change the person stopped keeps its ending under each turn.
  return !!entry.taskId && !entry.live && !!outcomeOwner && outcomeOwner !== entry.key && storyKind !== "failed" &&
    (storyKind !== "stopped" || superseded) && storyKind !== "answered";
}

export const MOVED_ON_LABEL = "Updated in a later turn";
export const MOVED_ON_EARLIER_LABEL = "See this change above";

export function MovedOnLine({ targetKey, earlier = false }: { targetKey: string; earlier?: boolean }) {
  const jump = () => {
    const target = Array.from(document.querySelectorAll<HTMLElement>("[data-lane-v2-turn]")).find((el) => el.dataset.laneV2Turn === targetKey);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <button
      type="button"
      onClick={jump}
      data-lane-v2-moved-on={targetKey}
      className="self-start text-[12px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {earlier ? `${MOVED_ON_EARLIER_LABEL} ↑` : `${MOVED_ON_LABEL} ↓`}
    </button>
  );
}

/** What the pointer bar says for the decision it points at. */
export function decisionWaitingLabel(kind: string): string {
  // lane v2 names its plan ask `approve_plan_revision` (and an amendment `approve_amendment`); the run lane's is `approve_plan`
  if (kind === "approve_plan" || kind === "approve_plan_revision" || kind === "approve_amendment") return "A plan is waiting for your approval";
  if (kind === "approve_changeset") return "Changes are waiting for your review";
  if (kind.startsWith("approve")) return "Waiting for your approval";
  return "Weave asked you a question";
}

/**
 * True while the element `selector` names is out of the viewport (an IntersectionObserver; `refresh` re-finds it when the list
 * re-renders). False when there is nothing to watch, so the pointer never shows for a card that does not exist.
 */
function useOffscreen(selector: string | null, refresh: number): boolean {
  const [offscreen, setOffscreen] = useState(false);
  useEffect(() => {
    const el = selector ? document.querySelector(selector) : null;
    if (!el || typeof IntersectionObserver === "undefined") {
      setOffscreen(false);
      return;
    }
    const io = new IntersectionObserver(([e]) => setOffscreen(!e.isIntersecting), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, [selector, refresh]);
  return offscreen;
}

/**
 * The one-line pointer above the composer while the open decision is scrolled out of view: it says what is waiting and takes the
 * reader to the card. The card itself never leaves the conversation, so nothing is overlaid or cut.
 */
export function DecisionWaitingBar({ ask }: { ask: AskView }) {
  const id = ask.interaction.interaction_id;
  const jump = () => {
    const card = document.querySelector<HTMLElement>(`[data-lane-v2-ask="${id}"]`);
    card?.scrollIntoView({ behavior: "smooth", block: "center" });
    card?.querySelector<HTMLElement>("button[data-interaction-option]")?.focus({ preventScroll: true });
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-ma-warn-line bg-ma-warn-soft px-3 py-2" data-lane-v2-decision-bar={id}>
      <span className="flex min-w-0 items-center gap-2 text-[13px] font-medium text-foreground">
        <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-[var(--ma-state-warn-text)]" />
        <span className="truncate">{decisionWaitingLabel(ask.interaction.kind)}</span>
      </span>
      <button
        type="button"
        onClick={jump}
        className="shrink-0 rounded-md px-2 py-1 text-[13px] font-semibold text-foreground hover:bg-background/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Review
      </button>
    </div>
  );
}

/**
 * Settled outcomes of a turn that only answered or refused while a task rested: it carries the task's id but did nothing to it, so it
 * never takes the task's card (dev 2026-11-02: the card and its "Waiting for you" strip jumped under every new question).
 */
const NON_OWNING_OUTCOMES: ReadonlySet<string> = new Set(["answered", "control_refused"]);

/**
 * The (task, owning turn) pairs as one string: equal across streamed deltas, so memoised cards keep their identity. Only turns that
 * acted on a task own it; a task no such turn carries falls back to every turn that does, so no card is ever dropped.
 */
export function taskOwnersKey(entries: readonly { kind: string; key: string; taskId?: string | null; outcome?: string | null }[]): string {
  const carriers = entries.filter((e) => e.kind === "turn" && e.taskId);
  const acted = new Set(carriers.filter((e) => !NON_OWNING_OUTCOMES.has(e.outcome ?? "")).map((e) => e.taskId));
  return carriers
    .filter((e) => !acted.has(e.taskId) || !NON_OWNING_OUTCOMES.has(e.outcome ?? ""))
    .map((e) => `${e.taskId}\u0000${e.key}`)
    .join("\u0001");
}

export function LaneV2TranscriptView({
  state: rawState,
  org,
  onAnswerAsk,
  onStaleAsk,
  onOpenTask,
  onOpenTaskItem,
  onSaveBranch,
  onValidateTask,
  onRequestChanges,
  onDiscardTask,
  onReconnect,
  onAsk,
  onSend,
  onCancelTask,
  onHoldTask,
  onResumeTask,
  onOpenCode,
  onOpenWorkspace,
  activeSection,
  activeRef,
  className,
  compaction = null,
}: LaneV2TranscriptViewProps) {
  // A forward task has no deploy step, so its (legacy) approve_apply card is never rendered; a revert task keeps its own.
  const state = useMemo(
    () => {
      const asks = withoutForwardDeployAsks(rawState.asks, rawState.snapshots);
      return asks === rawState.asks ? rawState : { ...rawState, asks: [...asks] };
    },
    [rawState],
  );
  const entries = useMemo(() => conversationEntries(state), [state]);
  // The turn the compaction's summary ends at; the divider is drawn after it (none loaded = no divider).
  const compactedTurnId = useMemo(
    () => (compaction ? (state.turns.find((t) => t.turn_seq === compaction.turn_seq)?.turn_id ?? null) : null),
    [compaction, state.turns],
  );
  const last = useMemo(() => lastTurnEntry(entries), [entries]);
  const live = turnInFlight(state);
  const ctx: StoryContext = useMemo(
    () => ({ snapshots: state.snapshots, tasks: state.tasks, asks: state.asks, activity: state.activity, activeTurnId: state.activeTurnId }),
    [state.snapshots, state.tasks, state.asks, state.activity, state.activeTurnId],
  );
  // The newest pending ask is the decision. It sits in the conversation where it was raised; the dock above the composer carries
  // only a one-line pointer to it, and only while it is scrolled out of view (dev 2026-11-02: the docked card cut the chat in half).
  const pending = useMemo(() => pendingAsks(state.asks), [state.asks]);
  const docked = pending.length ? pending[pending.length - 1] : null;
  const decisionOffscreen = useOffscreen(docked ? `[data-lane-v2-ask="${docked.interaction.interaction_id}"]` : null, entries.length);

  /*
   * The newest exchange owns the viewport: a new send pins the question to the top and its story
   * grows beneath (`FollowMode` "pin"). Re-armed on a send (the newest echo) and on a session switch.
   */
  const newestLocal = state.localTurns.length ? state.localTurns[state.localTurns.length - 1].localId : "";
  const scroll = useFollowScroll(state.sessionId ? `${state.sessionId}|${newestLocal}` : null, "pin");

  /*
   * Built ONCE per `onOpenCode`, like `WeaveTranscript`: a fresh interceptor per render would break
   * `ProseBlock`'s memo and re-parse every past turn's markdown on every store update.
   */
  const entityLinks = useEntityLinks({ snapshots: state.snapshots, onOpenItem: onOpenTaskItem });
  const codeInterceptor = useMemo(() => {
    const base = buildCodeInterceptor(onOpenCode);
    if (!entityLinks) return base;
    return { ...base, components: { ...base.components, ...entityLinks } };
  }, [onOpenCode, entityLinks]);

  // The newest answer's own actions — the SAME helpers and pipeline the v1 host uses.
  const answerMarkdown = useMemo(() => answerMarkdownOf(last), [last]);
  const followUps = useMemo(() => {
    if (!answerMarkdown) return [];
    const entities = extractFollowUpEntities(answerMarkdown, []);
    // MSA leg `notion-e2e` (QA N3): an answer that read only a connected tool (Notion, Jira…) and names no Salesforce component
    // gets no Salesforce follow-ups ("Show callers", "Show tests" under a Notion page summary).
    if (entities.length === 0 && readsOnlyConnectedTools(splitSourcesTail(answerMarkdown))) return [];
    // a turn that drafted a connected-tool write is about that write, not a Salesforce component
    if (lastTurnRaisedConnectorWrite(entries)) return [];
    return followUpsForLane(null, entities);
  }, [answerMarkdown, entries]);
  const [exporting, setExporting] = useState(false);
  const onExport = useCallback(async () => {
    if (!answerMarkdown || exporting) return;
    setExporting(true);
    try {
      const body = await buildExportMarkdown(answerMarkdown);
      await downloadMarkdownAs(body, "md", "weave-answer", { title: "Weave answer" });
    } catch {
      toast.error("Couldn't export this answer.");
    } finally {
      setExporting(false);
    }
  }, [answerMarkdown, exporting]);

  const streamStatus = streamStatusViewFor(state);

  const renderAsk = (ask: AskView, updating: boolean) => (
    <LaneV2AskBlock
      key={ask.interaction.interaction_id}
      ask={ask}
      org={org}
      offline={state.offline}
      snapshot={ask.task_id ? state.snapshots[ask.task_id] ?? null : null}
      onOpenTask={onOpenTask}
      onOpenItem={onOpenTaskItem}
      onAnswerAsk={onAnswerAsk}
      onStaleAsk={onStaleAsk}
      // a message of the person's is being worked on (an answer revising this plan): the card waits, it is not answerable mid-revision.
      // Only a turn AFTER the card counts: the turn that raised it is still finishing when it appears, and reading that as "updating"
      // showed every new plan as "Updating the plan with your message" with the server's raw text (dev 2026-11-02).
      updating={updating}
      onSendAnswer={onSend}
    />
  );

  /*
   * Settled asks, grouped: a task-bound one belongs to the story of the turn that owns the task
   * (any turn whose R5 `task_id` is it); a task-less one trails the turn before it in the timeline.
   * Only an orphan (no turn at all before it) still renders on its own.
   */
  const storiedTasks = useMemo(() => new Set(entries.flatMap((e) => (e.kind === "turn" && e.taskId ? [e.taskId] : []))), [entries]);
  const recordsByTurn = useMemo(() => {
    const map = new Map<string, AskView[]>();
    let owner: string | null = null;
    for (const entry of entries) {
      if (entry.kind === "turn") {
        owner = entry.key;
        continue;
      }
      if (entry.ask.status === "pending" || !owner) continue;
      if (entry.ask.task_id && storiedTasks.has(entry.ask.task_id)) continue;
      map.set(owner, [...(map.get(owner) ?? []), entry.ask]);
    }
    return map;
  }, [entries, storiedTasks]);

  /*
   * One outcome card and one approved-plan block per task, however many turns carry its `task_id` (the build turn, a
   * "check again", a follow-up): the card sits on the NEWEST such turn (the task-end summary), the plan on the FIRST.
   * Only a turn's OWN R5 `task_id` counts, never `presentTurn`'s live fallback onto the newest open task.
   */
  // A primitive key, so a delta-only change (new `entries` identity, same owners) leaves `taskOwners` and the cards untouched.
  const ownersKey = taskOwnersKey(entries);
  const taskOwners = useMemo(() => {
    const outcome = new Map<string, string>();
    const plan = new Map<string, string>();
    for (const pair of ownersKey ? ownersKey.split("\u0001") : []) {
      const [taskId, key] = pair.split("\u0000");
      outcome.set(taskId, key);
      if (!plan.has(taskId)) plan.set(taskId, key);
    }
    return { outcome, plan };
  }, [ownersKey]);
  // The cost of ALL of a task's turns (the card is the task's summary), as a primitive so a streamed delta leaves the memoised cards alone.
  const usageKey = useMemo(() => {
    const sums = new Map<string, number>();
    for (const e of entries) if (e.kind === "turn" && e.taskId && e.usage) sums.set(e.taskId, (sums.get(e.taskId) ?? 0) + (e.usage.cost_usd ?? 0));
    return Array.from(sums, ([id, cost]) => `${id}\u0000${formatCost(cost)}`).join("\u0001");
  }, [entries]);
  const usageByTask = useMemo(() => new Map(usageKey ? usageKey.split("\u0001").map((pair) => pair.split("\u0000") as [string, string]) : []), [usageKey]);
  // Stable per task: a streamed delta changes neither snapshots nor the callbacks, so past turns' memo holds.
  const outcomes = useMemo(() => {
    const map = new Map<string, ReactNode>();
    for (const taskId of taskOwners.outcome.keys()) {
      const snap = state.snapshots[taskId];
      if (!showsOutcome(snap)) continue;
      // C11: while the dock asks to approve the changes, the card offers no competing Save; it says what comes first.
      const awaitingApproval = state.asks.some((a) => a.task_id === taskId && a.status === "pending" && a.interaction.kind === "approve_changeset");
      map.set(
        taskId,
        <LaneV2TaskOutcome
          snapshot={snap}
          awaitingApproval={awaitingApproval}
          onViewItem={onOpenTaskItem}
          onOpenTask={onOpenTask}
          onSaveBranch={onSaveBranch}
          onValidate={onValidateTask}
          onRequestChanges={onRequestChanges}
          onDiscard={onDiscardTask}
          usageLabel={usageByTask.get(taskId) ?? null}
          offline={state.offline}
        />,
      );
    }
    return map;
  }, [taskOwners, state.snapshots, state.asks, usageByTask, onOpenTaskItem, onOpenTask, onSaveBranch, onValidateTask, onRequestChanges, onDiscardTask, state.offline]);

  // the turn in flight and the task it is working on (the one its story shows), for the earlier turns of that task
  const liveEntry = entries.find((e): e is ConversationTurnEntry => e.kind === "turn" && !!e.live) ?? null;
  const liveTaskId = liveEntry ? (taskForTurn(liveEntry, ctx)?.task_id ?? null) : null;

  const renderTurn = (entry: ConversationTurnEntry) => {
    const story = presentTurn(entry, ctx);
    // A newer turn is working on THIS task right now (an answer revising its plan): this turn's "Waiting for you" is stale until that
    // turn ends, so it points at it instead (dev 2026-11-02: "Waiting for you" above, "Working" below, during the same update).
    const liveOnSameTask = !entry.live && liveEntry && liveEntry.key !== entry.key && !!story.taskId && liveTaskId === story.taskId;
    const newest = entry === last;
    // One voice: the outcome sentence only when no story strip is there to say it — and the server's
    // own words own the closing (dev `gui-reasons`): a turn that carries text gets no client sentence.
    const serverText = entry.turn.assistant.length > 0;
    const closing = story.kind === "answered" && entry.phase === "complete" ? turnClosingLine(entry.outcome, entry.errorCode, serverText) : null;
    // Sent again as the words alone: a turn that took messages while it ran shows them labelled (`readableUserText`).
    const userText = entry.turn.user ? plainUserText(entry.turn.user.parts.map(partText).join("").trim()) : "";
    // Q&A turns (no task) stream the reply as normal prose, the way a stored answer renders; a task's turn keeps the quiet draft box.
    const draftText = entry.live && entry.turnId && state.liveDraft?.turnId === entry.turnId && entry.turn.assistant.length === 0 ? state.liveDraft.text : null;
    const proseDraft = draftText && draftText.trim() && !entry.taskId ? draftText : null;
    const withAssistant = proseDraft ? { ...entry.turn, assistant: conversationTurn(entry.turnId as string, null, proseDraft, true).assistant } : entry.turn;
    const shownAnswer = withAssistant.user ? answerDisplay(withAssistant.user.parts.map(partText).join("")) : null;
    // a plan answer sent from the card's text box shows as the person typed it, under its question (display only)
    const turnForView = shownAnswer && withAssistant.user
      ? { ...withAssistant, user: { ...withAssistant.user, parts: [{ ...withAssistant.user.parts[0], body: { ...withAssistant.user.parts[0].body, text: shownAnswer } }] } }
      : withAssistant;
    // The outcome card: the turn's own task, from `changeset_proposed` onward, on the task's newest turn only.
    const ownTaskId = entry.taskId;
    const taskSnapshot = ownTaskId ? state.snapshots[ownTaskId] : null;
    const outcomeCard = ownTaskId && taskOwners.outcome.get(ownTaskId) === entry.key ? (outcomes.get(ownTaskId) ?? null) : null;
    const outcomeOwner = ownTaskId ? taskOwners.outcome.get(ownTaskId) : undefined;
    const superseded = taskSnapshot?.state === "cancelled" && taskSnapshot?.ended?.cause === "superseded";
    const movedOnTo = liveOnSameTask ? (liveEntry as { key: string }).key
      : storyMovedOn(entry, story.kind, outcomeOwner, superseded) ? (outcomeOwner as string) : null;
    // A turn that only answered or refused while the task rested carries its id but did nothing to it: the task's strip belongs on
    // the turn that owns it, never under a question (dev 2026-11-02, "Replaced · Changes ready" under "What does … do?").
    const bystander = !entry.live && NON_OWNING_OUTCOMES.has(entry.outcome ?? "") && !!outcomeOwner && outcomeOwner !== entry.key;
    const approvedPlan =
      taskSnapshot && ownTaskId && taskOwners.plan.get(ownTaskId) === entry.key ? approvedPlanAsk(state.asks, ownTaskId) : null;
    // While a plan revision awaits an answer the snapshot's plan is the unapproved one: show the plain record instead.
    const revisionPending = !!ownTaskId && planAskPending(state.asks, ownTaskId);
    // Chronology (UX map 2.4): the plan was approved BEFORE the build, so its collapsed line sits above the outcome card (inside the
    // same slot) or, with no card yet, above the story strip. The task's newest turn shows the card; the plan stays on its first.
    const planBlock =
      approvedPlan && taskSnapshot
        ? revisionPending
          ? <LaneV2AskRecord ask={approvedPlan} />
          : <LaneV2ApprovedPlan ask={approvedPlan} snapshot={taskSnapshot} onOpenTask={onOpenTask} onOpenItem={onOpenTaskItem} />
        : null;
    const outcome = outcomeCard ? (
      <>
        {planBlock}
        {outcomeCard}
      </>
    ) : null;
    // "Cancel task" lives on the turn that owns the task's newest card (or the live turn), never at rest.
    // One affordance per act: when the task offers Discard (the card owns it), the strip does not also offer Cancel.
    const discardOffered = !!story.taskId && (state.snapshots[story.taskId]?.actions ?? []).includes("discard");
    const saveRunning = !!story.taskId && state.snapshots[story.taskId]?.package?.branch_save_running != null;
    const cancelHere = !!story.taskId && story.cancellable && !discardOffered && !saveRunning && !!onCancelTask && (ownTaskId ? taskOwners.outcome.get(ownTaskId) === entry.key : entry.live);
    const cancelTask = cancelHere
      ? async () => {
          const out = await onCancelTask!(story.taskId as string);
          return out.cancelled ? null : cancelRefusalCopy(out.reason);
        }
      : undefined;
    const taskControlHere = !!story.taskId && (ownTaskId ? taskOwners.outcome.get(ownTaskId) === entry.key : entry.live);
    const holdTask = taskControlHere && story.controls.hold && onHoldTask ? () => onHoldTask(story.taskId as string) : undefined;
    const resumeTask = taskControlHere && story.controls.resume && onResumeTask ? () => onResumeTask(story.taskId as string) : undefined;
    const completionShown =
      newest && entry.phase === "complete" && !entry.failure && !!answerMarkdown && !story.live && (story.kind !== "waiting" || story.atRest) && !story.decision;
    const noSources = completionShown && readNothing(splitSourcesTail(answerMarkdown), entry.toolCalls ?? null);
    return (
      <div
        key={entry.key}
        className="flex flex-col gap-3"
        data-lane-v2-turn={entry.key}
      >
        <TurnView
          turn={turnForView}
          onOpenWorkspace={onOpenWorkspace}
          activeSection={activeSection}
          activeRef={activeRef}
          codeInterceptor={codeInterceptor}
          userAttachments={entry.attachments}
          sourcesTail={!entry.live}
          sourcesToolCalls={entry.toolCalls ?? null}
          outcome={outcome}
        />
        {!outcomeCard && planBlock}
        {movedOnTo && !bystander && (
          <MovedOnLine
            targetKey={movedOnTo}
            earlier={entries.findIndex((e) => e.key === movedOnTo) < entries.findIndex((e) => e.key === entry.key)}
          />
        )}
        {story.kind !== "answered" && !movedOnTo && !bystander && (
          <LaneV2TurnStory
            story={story}
            since={entry.live ? state.workingSince : story.since}
            usage={entry.usage}
            draft={proseDraft ? null : draftText}
            proseStreaming={!!proseDraft}
            onOpenDetails={story.taskId ? () => onOpenTask(story.taskId as string) : undefined}
            // "Try again" re-sends the request and "Ask what happened" sends its question; without a send path they fill the composer.
            // Only the newest failed turn offers it: an older one would re-send a stale request on top of the conversation.
            onRetry={newest && story.failure?.retryable && (onSend ?? onAsk) && userText ? () => (onSend ?? onAsk)!(userText) : undefined}
            requestTitle={userText}
            onAsk={onSend ?? onAsk}
            onCancelTask={cancelTask}
            onHoldTask={holdTask}
            onResumeTask={resumeTask}
            folded={!!outcomeCard}
          />
        )}
        {(recordsByTurn.get(entry.key) ?? []).map((ask) => (
          <LaneV2AskRecord
            key={ask.interaction.interaction_id}
            ask={ask}
            outcome={state.connectorWrites[ask.interaction.interaction_id] ?? null}
          />
        ))}
        {closing && closing.tone !== "ok" && <FailureLine text={closing.text} tone={closing.tone} refCode={closing.ref ?? null} />}
        {/* Where a `/compact` happened: after the last turn its summary covers. From the server, so a reload keeps it. */}
        {compaction && entry.turnId && compactedTurnId === entry.turnId && <CompactionDivider compaction={compaction} />}
        {/* A turn error is never hidden behind the server's text (dev `gui-reasons`), even on a non-failed outcome. */}
        {!closing && story.kind === "answered" && entry.phase === "complete" && entry.errorCode && (
          <FailureLine text={turnFailureWords(entry.errorCode)} tone="bad" refCode={entry.errorCode} />
        )}
        {entry.usage && story.kind === "answered" && !completionShown && <LaneV2TurnUsageChip usage={entry.usage} />}
        {/*
          How the newest answer ends: copy, export, next questions, and the feedback bar. Feedback is
          keyed by the turn id: a v2 turn IS a `multi_agent_runs` row and MSA's feedback route checks
          ownership only (`routers/feedback.py`). An unacknowledged echo has no id and gets no bar.
        */}
        {/* …but not while a decision is pending: the next action is the card, not a follow-up. */}
        {completionShown && (
          <TurnCompletion
            runId={entry.turnId}
            closing={closing?.tone === "ok" ? closing.text : null}
            // After a failure the next move is "Ask what happened" in the story, not a generic follow-up.
            followUps={story.kind === "failed" || story.kind === "stopped" ? [] : followUps}
            onAsk={onAsk}
            onExport={onExport}
            exporting={exporting}
            answerMarkdown={answerMarkdown}
            usageLabel={entry.usage && !outcomeCard ? formatCost(entry.usage.cost_usd) : null}
            noSourcesLabel={noSources ? NO_SOURCES_LABEL : null}
          />
        )}
      </div>
    );
  };

  return (
    // A lane-v2 answer ends with the list of what it read, each line an `artifact://` link: here each is a chip that opens it.
    <ArtifactLinkContext.Provider value={renderEvidenceLink}>
    <TranscriptColumn
      scroll={scroll}
      live={live}
      className={className}
      dock={docked && decisionOffscreen && !live ? <DecisionWaitingBar ask={docked} /> : null}
    >
      <div data-lane-v2-session={state.sessionId ?? ""} className="contents">
        <StreamStatusView {...streamStatus} onRetry={onReconnect} lostDetail={SESSION_STREAM_LOST_DETAIL} />
        {state.offline && <OfflineBanner />}
        {state.error && (
          <p role="alert" className="text-[13px] text-ma-err-text" data-lane-v2-error="">
            {state.error}
          </p>
        )}

        {(() => {
          const renderEntry = (entry: (typeof entries)[number], i: number) => {
            if (entry.kind === "turn") return renderTurn(entry);
            const ask = entry.ask;
            if (ask.status !== "pending") {
              // Grouped under a turn or into a story above; only an orphan with no turn before it stays here.
              const orphan = !entries.slice(0, i).some((e) => e.kind === "turn") && !(ask.task_id && storiedTasks.has(ask.task_id));
              return orphan ? (
                <LaneV2AskRecord key={entry.key} ask={ask} outcome={state.connectorWrites[ask.interaction.interaction_id] ?? null} />
              ) : null;
            }
            return renderAsk(ask, entries.slice(i + 1).some((e) => e.kind === "turn" && e.live));
          };
          /*
           * ONE BLOCK PER EXCHANGE: a turn and the cards raised after it, keyed by the turn. The NEWEST block owns the viewport: its
           * question pins to the top and its story grows beneath, so it reserves the column's measured height (`--ma-col-h`, less
           * the column's top padding). Reserving on the turn alone pushed a waiting plan card a screen below its "Waiting for you"
           * (dev 2026-11-02, after #423); one block that took the newest entries re-mounted the previous turn on every send (an
           * expanded question collapsed), so each exchange keeps its own block and only the reserved height moves.
           */
          const groups: { key: string; turn: boolean; items: Array<[(typeof entries)[number], number]> }[] = [];
          entries.forEach((e, i) => {
            if (e.kind === "turn" || !groups.length) groups.push({ key: e.key, turn: e.kind === "turn", items: [] });
            groups[groups.length - 1].items.push([e, i]);
          });
          return groups.map((g) => {
            if (!g.turn) return g.items.map(([e, i]) => renderEntry(e, i));
            const newest = g.items[0][0] === last;
            return (
              <div
                key={`x-${g.key}`}
                data-weave-exchange=""
                data-weave-pin={newest ? "" : undefined}
                className="flex flex-col gap-7"
                style={newest ? { minHeight: "calc(var(--ma-col-h, 100vh) - 28px)" } : undefined}
              >
                {g.items.map(([e, i]) => renderEntry(e, i))}
              </div>
            );
          });
        })()}

        {entries.length === 0 && state.tasks.length === 0 && <EmptyConversation onAsk={onAsk} />}
      </div>
    </TranscriptColumn>
    </ArtifactLinkContext.Provider>
  );
}

// ── the connected component ──────────────────────────────────────────────────────────────────────

export interface LaneV2TranscriptProps {
  onAsk?: (question: string) => void;
  /** Send a message now ("Try again" / "Ask what happened"); omitted, those fill the composer through `onAsk`. */
  onSend?: (text: string) => void;
  /** Omitted, a fenced block opens in the workspace's own viewer (the QA harness has no editor). */
  onOpenCode?: (payload: CodePayload) => void;
  className?: string;
}

export function LaneV2Transcript({ onAsk, onSend, onOpenCode, className }: LaneV2TranscriptProps) {
  const state = useLaneV2SessionStore(
    useShallow((s) => ({
      sessionId: s.sessionId,
      draft: s.draft,
      tasks: s.tasks,
      snapshots: s.snapshots,
      asks: s.asks,
      turns: s.turns,
      activeTurnId: s.activeTurnId,
      localTurns: s.localTurns,
      streamStatus: s.streamStatus,
      error: s.error,
      usageTotal: s.usageTotal,
      offline: s.offline,
      activity: s.activity,
      workingSince: s.workingSince,
      liveDraft: s.liveDraft,
      connectorWrites: s.connectorWrites,
    })),
  );
  const selectedOrg = useAuthStoreV1((s) => s.selectedOrg);
  const org = useMemo(
    () => (selectedOrg ? { is_production: isProductionEnvironment(selectedOrg.orgType), org_name: selectedOrg.orgName } : null),
    [selectedOrg],
  );
  const section = useWeaveWorkspaceStore(selectWorkspaceSection);
  const focusRef = useWeaveWorkspaceStore(selectWorkspaceRef);
  const toggleWorkspace = useWeaveWorkspaceStore((s) => s.toggle);
  const openTaskInWorkspace = useWeaveWorkspaceStore((s) => s.openTask);
  const branchSave = useLaneV2SessionStore((s) => s.branchSave);
  useLaneV2AutoOpen();
  const openCodeInWorkspace = useWeaveWorkspaceStore((s) => s.openCode);
  const openCode = onOpenCode ?? openCodeInWorkspace;

  // Back from sleep / offline: a disconnected stream re-attaches (full refetch, then stream).
  useEffect(() => {
    const retry = () => {
      const s = useLaneV2SessionStore.getState();
      if (s.streamStatus === "disconnected" && s.sessionId && !s.draft && document.visibilityState === "visible") {
        void s.reconnect();
      }
    };
    window.addEventListener("online", retry);
    document.addEventListener("visibilitychange", retry);
    return () => {
      window.removeEventListener("online", retry);
      document.removeEventListener("visibilitychange", retry);
    };
  }, []);

  const store = useLaneV2SessionStore.getState;
  // The same query the composer's `/compact` reads and writes, so the divider appears the moment a compact lands.
  const { compaction } = useSessionCompaction(state.sessionId, !state.draft && state.turns.length > 0);
  const onOpenWorkspace = useCallback(
    // A document opens the viewer; changeset / deploy openers land on the newest task; the rest have no surface.
    (next: OpenableSection, ref: string | null) => {
      const target = workspaceOpenFor(next, state.tasks);
      if (target?.kind === "artifact") toggleWorkspace("artifact", ref);
      else if (target?.kind === "task") openTaskInWorkspace(target.taskId);
    },
    [toggleWorkspace, openTaskInWorkspace, state.tasks],
  );

  const onOpenTask = useCallback((taskId: string, openedBy?: "auto" | "user") => openTaskInWorkspace(taskId, null, openedBy), [openTaskInWorkspace]);
  const onOpenTaskItem = useCallback((taskId: string, itemId: string) => openTaskInWorkspace(taskId, itemId), [openTaskInWorkspace]);
  const onSaveBranch = useMemo(
    () => (branchSave ? (id: string, body: SaveBranchIn, opts?: SaveBranchRunOpts) => useLaneV2SessionStore.getState().saveBranch(id, body, opts) : undefined),
    [branchSave],
  );
  const onValidateTask = useCallback((id: string, key: string) => useLaneV2SessionStore.getState().validateTask(id, key), []);
  const onRequestChanges = useCallback((id: string, text: string, key: string) => useLaneV2SessionStore.getState().requestChanges(id, text, key), []);
  const onDiscardTask = useCallback((id: string, key: string) => useLaneV2SessionStore.getState().discardTask(id, key), []);
  const onCancelTask = useCallback((id: string) => useLaneV2SessionStore.getState().cancelTask(id), []);
  const onHoldTask = useCallback((id: string) => useLaneV2SessionStore.getState().holdTask(id), []);
  const onResumeTask = useCallback((id: string) => useLaneV2SessionStore.getState().resumeTask(id), []);

  return (
    <LaneV2TranscriptView
      className={className}
      state={state}
      org={org}
      onAnswerAsk={(ask, body) => store().answerAsk(ask, body)}
      onStaleAsk={(ask) => void store().refetch({ session: true, asks: true, turns: false, tasks: ask.task_id ? [ask.task_id] : [] })}
      onOpenTask={onOpenTask}
      onOpenTaskItem={onOpenTaskItem}
      onSaveBranch={onSaveBranch}
      onValidateTask={onValidateTask}
      onRequestChanges={onRequestChanges}
      onDiscardTask={onDiscardTask}
      onReconnect={() => store().reconnect()}
      onAsk={onAsk}
      onSend={onSend}
      onCancelTask={onCancelTask}
      onHoldTask={onHoldTask}
      onResumeTask={onResumeTask}
      onOpenCode={openCode}
      onOpenWorkspace={onOpenWorkspace}
      activeSection={section}
      activeRef={focusRef}
      compaction={compaction}
    />
  );
}
